import { useSubsequentEffect } from '@carvana/merch-react-hooks';
import { useEffect, useState, useContext, useRef, useCallback } from 'react';
import { shareableCdnPath } from '~/constants/environment';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import DeviceContext, { useDeviceType } from '~/context/common/DeviceContext';
import releaseMediaElement from '~/utilities/releaseMediaElement';
import {
  VideoWrapper,
  Video,
  MiddlePlayButton,
  MediaPlayIcon
} from './Video.styles';

function assetUrlConstructor(assetId: string, assetType: string) {
  return `${shareableCdnPath}/${assetId}/${assetType}`;
}

type OwnProps = {
  assetId: string;
};

export default function VideoPlayer({ assetId }: OwnProps) {
  const newRideVideoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const trackEvent = useTrackEvent();
  const { isDesktop, isMobile } = useDeviceType();
  const browserName = useContext(DeviceContext).browserName.toLowerCase();

  const isChrome = browserName.includes('chrome');
  const isFirefox = browserName.includes('firefox');

  const assetThumbnailUrl = assetUrlConstructor(assetId, 'thumbnail.jpg');
  const shouldDisplayCustomControls =
    (isChrome || isFirefox) && isDesktop && !isPlaying;
  const isChromeDesktop = isChrome && isDesktop;
  const isChromeFullScreenOnDesktop = useCallback(
    () =>
      newRideVideoRef.current
        ? isChromeDesktop && newRideVideoRef.current.clientHeight > 450
        : false,
    [isChromeDesktop]
  );
  const videoSource = isMobile
    ? assetUrlConstructor(assetId, 'video_540.mp4')
    : assetUrlConstructor(assetId, 'video_720.mp4');

  useEffect(() => {
    function handlePlay() {
      setIsPlaying(true);
    }

    function handlePause() {
      setIsPlaying(false);
    }

    const newRideVideoEl = newRideVideoRef.current;

    newRideVideoEl?.addEventListener('play', handlePlay);
    newRideVideoEl?.addEventListener('pause', handlePause);

    return () => {
      newRideVideoEl?.removeEventListener('play', handlePlay);
      newRideVideoEl?.removeEventListener('pause', handlePause);
      releaseMediaElement(newRideVideoEl);
    };
  }, []);

  useSubsequentEffect(() => {
    trackEvent('New ride video', { action: isPlaying ? 'play' : 'pause' });
  }, [isPlaying, trackEvent]);

  const videoEventHandler = useCallback(async () => {
    if (isPlaying) {
      if (!isChromeDesktop) {
        newRideVideoRef.current?.pause();
      }
    } else {
      if (!isChromeFullScreenOnDesktop()) {
        await newRideVideoRef.current?.play();
      }
    }
  }, [isChromeDesktop, isChromeFullScreenOnDesktop, isPlaying]);

  return (
    <VideoWrapper>
      <Video
        ref={newRideVideoRef}
        controls={!shouldDisplayCustomControls}
        poster={assetThumbnailUrl}
        muted
        onClick={videoEventHandler}
        onTouchStart={videoEventHandler}
        data-analytics-id="New ride video"
        data-testid="video-player"
      >
        <source src={videoSource} type="video/mp4" />;
      </Video>
      <MiddlePlayButton
        aria-label={isPlaying ? 'Pause video' : 'Play video'}
        className={isPlaying ? 'hidden' : ''}
        data-analytics-id="New ride video"
        data-testid="play-video"
        onClick={videoEventHandler}
      >
        <MediaPlayIcon className="PlayButton" />
      </MiddlePlayButton>
    </VideoWrapper>
  );
}
